import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Search, Loader2, ImagePlus, X, ShoppingCart, Home } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import logo from "@/assets/logo.png";
import PriceDisplay, { type CheckedGrade } from "@/components/PriceDisplay";
import type { Deal, DealItem } from "@/types/deal";

interface PriceResult {
  name: string;
  platform: string;
  imageUrl: string;
  url: string;
  prices: {
    loose: string | null;
    complete: string | null;
    new: string | null;
    graded: string | null;
    boxOnly: string | null;
    manualOnly: string | null;
  };
}

const Index = () => {
  const [upc, setUpc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PriceResult | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [dealName, setDealName] = useState("");
  const [dealId, setDealId] = useState<string | null>(null);
  const [dealBatchCode, setDealBatchCode] = useState<string | null>(null);
  const [dealCreatedAt, setDealCreatedAt] = useState<string | null>(null);
  const [dealItems, setDealItems] = useState<DealItem[]>([]);
  const [currentChecked, setCurrentChecked] = useState<CheckedGrade[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const lookupPrice = async (code: string) => {
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    setCurrentChecked([]);

    try {
      const { data, error } = await supabase.functions.invoke("price-lookup", {
        body: { upc: code.trim() },
      });

      if (error) throw error;

      if (data?.success && data?.data) {
        setResult(data.data);
      } else {
        toast({
          title: "Not Found",
          description: data?.error || "No product found for this barcode.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("Lookup error:", err);
      toast({
        title: "Error",
        description: "Failed to look up price. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScan = (code: string) => {
    setUpc(code);
    lookupPrice(code);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    lookupPrice(upc);
  };

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPhotoUrl(url);
    }
  };

  const clearPhoto = () => {
    setPhotoUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Ensure a deal row exists in the DB; returns its id or null.
  const ensureDeal = async (): Promise<string | null> => {
    if (dealId) return dealId;
    if (!dealName.trim()) {
      toast({ title: "Name Required", description: "Enter a deal name first.", variant: "destructive" });
      return null;
    }
    try {
      const { data, error } = await supabase
        .from("deals")
        .insert({ name: dealName.trim() })
        .select()
        .single();

      if (error) {
        if ((error as any).code === "23505") {
          toast({ title: "Duplicate Name", description: "A deal with this name already exists.", variant: "destructive" });
        } else {
          throw error;
        }
        return null;
      }

      setDealId(data.id);
      setDealBatchCode(data.batch_code);
      setDealCreatedAt(data.created_at);
      return data.id;
    } catch (err) {
      console.error("Create deal error:", err);
      toast({ title: "Error", description: "Failed to create deal.", variant: "destructive" });
      return null;
    }
  };

  const addToDeal = async () => {
    if (currentChecked.length === 0) {
      toast({ title: "Select Items", description: "Check at least one grade to add to the deal.", variant: "destructive" });
      return;
    }

    const id = await ensureDeal();
    if (!id) return;

    const itemsToInsert = currentChecked.map((c) => ({
      deal_id: id,
      name: c.productName,
      platform: c.platform,
      image_url: c.imageUrl,
      photo_url: c.photoUrl,
      price_type: c.priceType,
      original_price: c.originalPrice,
      grade: c.grade,
      grade_price: c.gradePrice,
      upc: upc || null,
    }));

    try {
      const { data: inserted, error } = await supabase
        .from("deal_items")
        .insert(itemsToInsert)
        .select();
      if (error) throw error;

      const newItems: DealItem[] = (inserted || []).map((i: any) => ({
        id: i.id,
        name: i.name,
        platform: i.platform || "",
        imageUrl: i.image_url || "",
        photoUrl: i.photo_url,
        priceType: i.price_type,
        originalPrice: i.original_price,
        grade: i.grade,
        gradePrice: i.grade_price,
        upc: i.upc || "",
      }));

      setDealItems((prev) => [...prev, ...newItems]);
      setCurrentChecked([]);
      setResult(null);
      setPhotoUrl(null);
      setUpc("");
      toast({ title: "Saved!", description: `${newItems.length} item(s) saved to deal.` });
    } catch (err) {
      console.error("Save item error:", err);
      toast({ title: "Error", description: "Failed to save item.", variant: "destructive" });
    }
  };

  const viewDeal = () => {
    if (!dealId) {
      toast({ title: "No Deal", description: "Add an item to create the deal first.", variant: "destructive" });
      return;
    }

    const deal: Deal = {
      id: dealId,
      batchCode: dealBatchCode || "",
      name: dealName.trim(),
      approved: false,
      items: dealItems,
      createdAt: dealCreatedAt || new Date().toISOString(),
    };

    setDealItems([]);
    setDealName("");
    setDealId(null);
    setDealBatchCode(null);
    setDealCreatedAt(null);
    navigate("/deal", { state: { deal } });
  };

  const dealTotal = dealItems.reduce((s, i) => s + parseFloat(i.gradePrice), 0);

  return (
    <div className="min-h-screen bg-background relative">
      <div className="fixed inset-0 pointer-events-none scanline opacity-30 z-50" />

      <div className="relative z-10 max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="text-center space-y-1">
          <button
            onClick={() => navigate("/")}
            className="absolute top-4 left-4 flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-primary transition-colors"
          >
            <Home className="w-4 h-4" />
            HOME
          </button>
          <div className="flex items-center justify-center">
            <img src={logo} alt="Lynx to the Past" className="w-full max-w-xs" />
          </div>
          <p className="text-xs font-mono text-muted-foreground tracking-wider">
            SCAN • LOOKUP • PRICE CHECK
          </p>
        </header>

        {/* Deal Name */}
        <div className="p-3 bg-card rounded-lg border border-border space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-mono text-muted-foreground tracking-widest">DEAL NAME</label>
            <Button
              onClick={() => navigate("/lookup")}
              variant="ghost"
              size="sm"
              className="h-auto py-0 px-2 text-[10px] font-mono text-primary hover:text-primary/80"
            >
              <Search className="w-3 h-3 mr-1" /> LOOKUP
            </Button>
          </div>
          <Input
            value={dealName}
            onChange={(e) => setDealName(e.target.value)}
            disabled={!!dealId}
            placeholder="Enter deal / batch name..."
            className="font-mono bg-background border-border focus:border-primary text-foreground placeholder:text-muted-foreground disabled:opacity-70"
          />
          {dealId && (
            <p className="text-[10px] font-mono text-primary tracking-wider">✓ DEAL CREATED • {dealBatchCode}</p>
          )}
          {dealItems.length > 0 && (
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-mono text-muted-foreground">
                {dealItems.length} item(s) • ${dealTotal.toFixed(2)}
              </span>
              <Button onClick={viewDeal} size="sm" className="bg-primary text-primary-foreground font-mono text-xs">
                <ShoppingCart className="w-3 h-3 mr-1" /> VIEW DEAL
              </Button>
            </div>
          )}
        </div>

        {/* Scanner */}
        <BarcodeScanner onScan={handleScan} isScanning={isScanning} setIsScanning={setIsScanning} />

        {/* Photo Capture */}
        <div className="flex items-center gap-2">
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoCapture} className="hidden" />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 border-primary/40 text-primary font-mono text-xs tracking-wider"
          >
            <ImagePlus className="w-4 h-4 mr-2" /> TAKE PHOTO OF ITEM
          </Button>
          {photoUrl && (
            <Button type="button" variant="ghost" size="sm" onClick={clearPhoto} className="text-muted-foreground">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        {photoUrl && (
          <div className="flex justify-center">
            <img src={photoUrl} alt="Item photo" className="w-32 h-32 object-contain rounded border border-border bg-muted" />
          </div>
        )}

        {/* Manual Input */}
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <Input
            value={upc}
            onChange={(e) => setUpc(e.target.value)}
            placeholder="Enter UPC / barcode..."
            className="font-mono bg-card border-border focus:border-primary text-foreground placeholder:text-muted-foreground"
          />
          <Button type="submit" disabled={loading || !upc.trim()} className="bg-primary text-primary-foreground font-mono font-bold shrink-0">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </form>

        {/* Loading */}
        {loading && (
          <div className="text-center py-8 space-y-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            <p className="font-mono text-sm text-primary animate-pulse-glow">SCANNING DATABASE...</p>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            <PriceDisplay data={result} photoUrl={photoUrl} onCheckedChange={setCurrentChecked} />
            {currentChecked.length > 0 && (
              <Button onClick={addToDeal} className="w-full bg-primary text-primary-foreground font-mono font-bold tracking-wider">
                SAVE {currentChecked.length} ITEM(S) TO DEAL
              </Button>
            )}
          </>
        )}

        {/* Footer */}
        <footer className="text-center pt-4">
          <p className="text-[10px] font-mono text-muted-foreground/50">DATA FROM PRICECHARTING.COM</p>
        </footer>
      </div>
    </div>
  );
};

export default Index;
